// src/components/PaymentModal.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCreditCard, FiTag, FiShield, FiCheck, FiLoader } from 'react-icons/fi';
import api from '../utils/apiClient';
import toast from 'react-hot-toast';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled(motion.div)`
  background: ${({ theme }) => theme.colors.background};
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const Header = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  justify-content: between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundLighter};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Content = styled.div`
  padding: 24px;
`;

const CourseInfo = styled.div`
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const CourseTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px 0;
`;

const CourseDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
`;

const PricingSection = styled.div`
  margin-bottom: 24px;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
    padding-top: 12px;
    border-top: 1px solid ${({ theme }) => theme.colors.divider};
    font-weight: 600;
  }
`;

const PriceLabel = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
`;

const PriceValue = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
  
  &.original {
    text-decoration: line-through;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  
  &.discount {
    color: ${({ theme }) => theme.colors.success};
  }
  
  &.total {
    font-size: 18px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const CouponSection = styled.div`
  margin-bottom: 24px;
`;

const CouponInput = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
`;

const Input = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.backgroundLight};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  
  &.primary {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.primaryDark};
    }
  }
  
  &.secondary {
    background: ${({ theme }) => theme.colors.backgroundLight};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.divider};
    
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.backgroundLighter};
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PayButton = styled(Button)`
  width: 100%;
  padding: 16px;
  font-size: 16px;
  font-weight: 600;
  margin-top: 24px;
`;

const CouponStatus = styled.div`
  margin-top: 12px;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &.success {
    background: ${({ theme }) => theme.colors.success}20;
    color: ${({ theme }) => theme.colors.success};
    border: 1px solid ${({ theme }) => theme.colors.success}40;
  }
  
  &.error {
    background: ${({ theme }) => theme.colors.error}20;
    color: ${({ theme }) => theme.colors.error};
    border: 1px solid ${({ theme }) => theme.colors.error}40;
  }
`;

const FeaturesList = styled.div`
  margin-top: 24px;
  padding: 20px;
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 12px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  svg {
    color: ${({ theme }) => theme.colors.success};
    flex-shrink: 0;
  }
`;

const PaymentModal = ({ isOpen, onClose, course, onSuccess }) => {
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) {
      return '0';
    }

    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return Number.isFinite(numericAmount) ? numericAmount.toLocaleString() : '0';
  };

  const basePrice = pricing?.price ?? 0;
  const discountAmount = pricing?.discount ?? 0;
  const payableAmount = pricing?.final_price ?? pricing?.price ?? 0;
  const hasDiscount = Number(discountAmount) > 0;
  const resolvedCouponCode = appliedCoupon?.code || (couponCode.trim() ? couponCode.trim().toUpperCase() : null);

  useEffect(() => {
    if (isOpen && course) {
      fetchCoursePricing();
    }
  }, [isOpen, course]);

  const fetchCoursePricing = async () => {
    try {
      const response = await api.payment.getCoursePricing(course.id);
      setPricing(response);
    } catch (error) {
      console.error('Error fetching course pricing:', error);
      toast.error('Failed to load course pricing');
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponLoading(true);
    setCouponError('');
    
    try {
      const response = await api.payment.validateCoupon(course.id, couponCode.trim());
      
      if (response.coupon_valid) {
        setAppliedCoupon({
          code: couponCode.trim(),
          discount: response.discount,
          finalPrice: response.final_price
        });
        setPricing(prev => ({
          ...prev,
          discount: response.discount,
          final_price: response.final_price
        }));
        toast.success('Coupon applied successfully!');
      } else {
        setCouponError(response.error || 'Invalid coupon code');
      }
    } catch (error) {
      setCouponError('Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
    setPricing(prev => ({
      ...prev,
      discount: 0,
      final_price: prev.price
    }));
  };

  const handlePayment = async () => {
    setLoading(true);
    
    try {
      const isZeroDueToCoupon = payableAmount <= 0 && (appliedCoupon || Number(basePrice) <= 0);

      if (isZeroDueToCoupon) {
        try {
          await api.courses.enroll(
            course.id,
            resolvedCouponCode ? { coupon_code: resolvedCouponCode } : {}
          );
          toast.success('Course unlocked for free!');
          onSuccess();
          onClose();
        } catch (error) {
          console.error('Free enrollment failed:', error);
          toast.error('Could not unlock the course automatically. Please contact support.');
        } finally {
          setLoading(false);
        }
        return;
      }

      // Create order
      const orderResponse = await api.payment.createOrder(
        course.id, 
        appliedCoupon?.code || null
      );

      if (orderResponse.message && orderResponse.message.includes('free')) {
        if (resolvedCouponCode) {
          try {
            await api.courses.enroll(course.id, { coupon_code: resolvedCouponCode });
          } catch (error) {
            console.error('Post-order enrollment failed:', error);
          }
        }
        // Course became free with coupon
        toast.success('Course enrolled successfully!');
        onSuccess();
        onClose();
        setLoading(false);
        return;
      }

      if (orderResponse.requires_payment === false || (typeof orderResponse.amount === 'number' && orderResponse.amount <= 0)) {
        if (resolvedCouponCode) {
          try {
            await api.courses.enroll(course.id, { coupon_code: resolvedCouponCode });
          } catch (error) {
            console.error('Post-order enrollment failed:', error);
          }
        }
        toast.success('Course unlocked for free!');
        onSuccess();
        onClose();
        setLoading(false);
        return;
      }

      // Initialize Razorpay
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderResponse.amount * 100, // Convert to paisa
        currency: orderResponse.currency,
        name: 'CHIEF Cybersecurity',
        description: `Purchase: ${course.title}`,
        order_id: orderResponse.razorpay_order_id,
        handler: async function(response) {
          try {
            // Verify payment
            const verifyResponse = await api.payment.verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature
            });
            
            toast.success('Payment successful! Course unlocked.');
            onSuccess();
            onClose();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: 'Student',
          email: 'student@example.com'
        },
        theme: {
          color: '#0AFF70'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Header>
            <Title>Complete Purchase</Title>
            <CloseButton onClick={onClose}>
              <FiX size={24} />
            </CloseButton>
          </Header>

          <Content>
            <CourseInfo>
              <CourseTitle>{course?.title}</CourseTitle>
              <CourseDescription>{course?.description}</CourseDescription>
            </CourseInfo>

            {pricing && (
              <PricingSection>
                <PriceRow>
                  <PriceLabel>Course Price</PriceLabel>
                  <PriceValue className={hasDiscount ? 'original' : ''}>
                    ₹{formatAmount(basePrice)}
                  </PriceValue>
                </PriceRow>
                
                {hasDiscount && (
                  <PriceRow>
                    <PriceLabel>Discount</PriceLabel>
                    <PriceValue className="discount">
                      -₹{formatAmount(discountAmount)}
                    </PriceValue>
                  </PriceRow>
                )}
                
                <PriceRow>
                  <PriceLabel>Total Amount</PriceLabel>
                  <PriceValue className="total">
                    ₹{formatAmount(payableAmount)}
                  </PriceValue>
                </PriceRow>
              </PricingSection>
            )}

            <CouponSection>
              <PriceLabel>Have a coupon code?</PriceLabel>
              {!appliedCoupon ? (
                <CouponInput>
                  <Input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && applyCoupon()}
                  />
                  <Button
                    type="button"
                    className="secondary"
                    onClick={applyCoupon}
                    disabled={!couponCode.trim() || couponLoading}
                  >
                    {couponLoading ? <FiLoader className="animate-spin" /> : <FiTag />}
                    Apply
                  </Button>
                </CouponInput>
              ) : (
                <CouponStatus className="success">
                  <FiCheck />
                  Coupon "{appliedCoupon.code}" applied! 
                  <Button
                    type="button"
                    className="secondary"
                    onClick={removeCoupon}
                    style={{ marginLeft: 'auto', padding: '4px 8px', fontSize: '12px' }}
                  >
                    Remove
                  </Button>
                </CouponStatus>
              )}
              
              {couponError && (
                <CouponStatus className="error">
                  <FiX />
                  {couponError}
                </CouponStatus>
              )}
            </CouponSection>

            {/* <FeaturesList>
              <FeatureItem>
                <FiCheck />
                Lifetime access to course content
              </FeatureItem>
              <FeatureItem>
                <FiCheck />
                Interactive labs and quizzes
              </FeatureItem>
              {pricing?.requires_lab_access && (
                <FeatureItem>
                  <FiShield />
                  1 year VM lab access (Kali Linux + Vulnerable machines)
                </FeatureItem>
              )}
              <FeatureItem>
                <FiCheck />
                Certificate of completion
              </FeatureItem>
            </FeaturesList> */}

            <PayButton
              className="primary"
              onClick={handlePayment}
              disabled={loading || !pricing}
            >
              {loading ? (
                <>
                  <FiLoader className="animate-spin" />
                  Processing...
                </>
              ) : payableAmount <= 0 ? (
                <>
                  <FiCheck />
                  Unlock for Free
                </>
              ) : (
                <>
                  <FiCreditCard />
                      Pay ₹{formatAmount(payableAmount)}
                </>
              )}
            </PayButton>
          </Content>
        </Modal>
      </Overlay>
    </AnimatePresence>
  );
};

export default PaymentModal;
